/**
 * Liabilities — what the company owes its vendors.
 *
 * THE ONE RULE THIS SCREEN OBEYS
 *
 * It does not invent a second definition of "outstanding". Every figure here
 * rolls up v_payable_settlement.outstanding_amount — the same number the
 * Payables screen shows. If this dashboard and that register ever disagreed,
 * one would be wrong and nobody would know which.
 *
 * So a payment recorded on the Payables screen moves these charts on the next
 * refresh, automatically. There is no separate liability ledger to fall out of
 * step, because there is no separate ledger at all.
 *
 * WHY THE TREND SAYS "reconstructed" ON THE OLDER POINTS
 *
 * Nobody recorded when billing was closed on each payable, so a perfect
 * restatement of a past month is not possible from what was stored. The older
 * points are rebuilt from the dates that WERE stored — receipt, payment,
 * reconciliation, credit note — and the arithmetic is anchored so the latest
 * point equals today's exact figure. From today a daily snapshot is taken, and
 * those points are marked exact.
 *
 * Saying which is which on the chart matters. A finance director reading a
 * reconstructed figure as though it were a closed-book number is how a board
 * paper ends up wrong.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Area, Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import {
  AlertTriangle, Building2, Download, HardHat, Info, RefreshCw, Truck, Wallet,
} from 'lucide-react'
import {
  Alert, Card, Loading, Select, Stat, Th, sortRows, useSort, useToast,
} from '@/components/ui'
import { NotPermitted } from '@/components/docs/shared'
import { useAuth } from '@/lib/auth'
import { useQuery } from '@/lib/data'
import { downloadFromFunction, supabase } from '@/lib/supabase'
import { rupees, ddmmyyyy } from '@/lib/format'
import { useCompanySettings } from '@/lib/settings'
import { CAN } from '@/lib/domain'

// A restrained palette. Liabilities are not a celebration — the only saturated
// colour on the screen is the one that means "overdue".
const OVAL = '#3C55A5'
const OVAL_LIGHT = '#9AA8D5'
const RED = '#C2414A'
const SLATE = '#9AA6A2'
const AGE_COLOURS: Record<string, string> = {
  'Not yet due': '#0f766e',
  '1-30 days overdue': '#71807C',
  '31-60 days overdue': '#806A45',
  '61-90 days overdue': '#6B583A',
  '90+ days overdue': '#C2414A',
}

type Company = {
  open_items: number; vendors: number; sites: number
  total_owed: number; invoiced_owed: number; uninvoiced_owed: number
  overdue_owed: number; overdue_items: number; oldest_days: number | null
}
type Site = {
  site_id: string; site_name: string; pm_id: string | null; pm_name: string | null
  open_items: number; vendors: number
  total_owed: number; invoiced_owed: number; uninvoiced_owed: number
  overdue_owed: number; oldest_days: number | null
}
type Pm = {
  pm_key: string; pm_name: string; open_items: number; sites: number; vendors: number
  total_owed: number; invoiced_owed: number; uninvoiced_owed: number
  overdue_owed: number; oldest_days: number | null
}
type Vendor = {
  vendor_id: string; vendor_name: string; vendor_gstin: string | null
  open_items: number; sites: number
  total_owed: number; invoiced_owed: number; uninvoiced_owed: number
  overdue_owed: number; oldest_days: number | null; next_due: string | null
}
type Ageing = { ageing_bucket: string; bucket_order: number; open_items: number; total_owed: number }
type TrendPoint = { period_end: string; period_label: string; total_owed: number; source: string }

/** ₹ in lakh on an axis. A crore-scale rupee figure is unreadable in full. */
const lakh = (n: number) => {
  const v = Number(n) || 0
  if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`
  if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(1)}L`
  if (Math.abs(v) >= 1e3) return `₹${(v / 1e3).toFixed(0)}k`
  return `₹${v.toFixed(0)}`
}

function ChartTip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-lg">
      <p className="mb-1 text-xs font-semibold text-slate-700">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} className="text-xs" style={{ color: p.color ?? p.fill }}>
          {p.name}: <span className="font-semibold tabular-nums">{rupees(p.value)}</span>
        </p>
      ))}
    </div>
  )
}

export default function Liabilities() {
  const { profile } = useAuth()
  const toast = useToast()
  const role = profile?.role
  const mayUse = role ? CAN.viewLiabilities.includes(role) : false

  /**
   * The daily snapshot is taken automatically at 22:00 IST by a scheduled job
   * in the database. Nobody has to remember it.
   *
   * The button that does it by hand is therefore kept for the administrator
   * only, as a fallback for the evening the job does not run. It used to show
   * to everyone who could see this screen — including the CMD, on a dashboard
   * he opens to read a number, not to operate. A write action on a read-only
   * board screen is an invitation to a wrong click, and its absence is one
   * less thing for a busy person to wonder about.
   */
  const mayRecord = role === 'admin'

  /**
   * A Project Manager sees this screen as a PORTFOLIO, not as a board panel.
   *
   * Row level security does the actual narrowing — vendor_payables is scoped
   * to the sites he is named on, and v_payable_settlement honours it since
   * migration 20261024000001 — so every figure here is already his and his
   * alone. Nothing on this page had to learn about him.
   *
   * What DID have to change is what the page CALLS those figures. A total
   * computed from three sites, sitting under a heading that says "company-
   * wide", is a screen telling a lie it does not know it is telling. He is
   * exactly the person who would repeat that number in a meeting.
   *
   * So the wording changes, and the trend defaults to his own portfolio rather
   * than to COMPANY. The database narrows a COMPANY request from him anyway;
   * this just means the dropdown agrees with what comes back.
   */
  const isPm = role === 'project_manager'

  /**
   * Has the nightly job actually run?
   *
   * Read here rather than relying on the job to report its own failure — a job
   * that did not run cannot tell anybody it did not run. This check needs no
   * scheduler, so it still works on the day the scheduler is the broken thing.
   */
  const { data: health, refresh: refreshHealth } =
    useQuery<any>('v_liability_snapshot_health', () =>
      supabase.from('v_liability_snapshot_health').select('*'), [], { cacheable: false })
  const snap = health?.[0]

  const [scope, setScope] = useState<'COMPANY' | 'SITE' | 'PM' | 'VENDOR'>(
    role === 'project_manager' ? 'PM' : 'COMPANY')
  const [scopeId, setScopeId] = useState<string>('')
  const [months, setMonths] = useState(12)
  const [snapping, setSnapping] = useState(false)
  const [exporting, setExporting] = useState<string | null>(null)
  const { sort: siteSort, toggle: siteToggle } = useSort('total_owed', 'desc')
  const { sort: pmSort, toggle: pmToggle } = useSort('total_owed', 'desc')
  const { sort: venSort, toggle: venToggle } = useSort('total_owed', 'desc')

  const q = { cacheable: false }
  const { data: company, loading: l1 } = useQuery<Company>(
    'v_liability_company', () => supabase.from('v_liability_company').select('*'), [], q)
  const { data: sites, loading: l2 } = useQuery<Site>(
    'v_liability_by_site', () => supabase.from('v_liability_by_site').select('*'), [],
    { ...q, idField: 'site_id' })
  const { data: pms } = useQuery<Pm>(
    'v_liability_by_pm', () => supabase.from('v_liability_by_pm').select('*'), [],
    { ...q, idField: 'pm_key' })
  const { data: vendors } = useQuery<Vendor>(
    'v_liability_by_vendor', () => supabase.from('v_liability_by_vendor').select('*'), [],
    { ...q, idField: 'vendor_id' })
  const { data: ageing } = useQuery<Ageing>(
    'v_liability_ageing', () => supabase.from('v_liability_ageing').select('*'), [],
    { ...q, idField: 'ageing_bucket' })
  const { data: trend, loading: l3, refresh: refreshTrend } = useQuery<TrendPoint>(
    'liability_trend',
    () => supabase.rpc('fn_liability_trend', {
      p_scope: scope, p_scope_id: scope === 'COMPANY' ? null : (scopeId || null), p_months: months,
    }),
    [scope, scopeId, months], { ...q, idField: 'period_end' })

  /**
   * Pick his own portfolio for him, so the trend draws on arrival.
   *
   * v_liability_by_pm returns exactly ONE row to a Project Manager — row level
   * security sees to that — so there is nothing to choose between, and asking
   * him to choose it from a list of one is a worse screen. Left to itself the
   * chart would open on "Choose one from the list above".
   *
   * Guarded on scopeId being empty so it never fights a choice he has made,
   * and on isPm so no other role is touched.
   */
  useEffect(() => {
    if (isPm && scope === 'PM' && !scopeId && pms.length === 1) {
      setScopeId(String(pms[0].pm_key))
    }
  }, [isPm, scope, scopeId, pms])

  const c = company[0]
  const sortedSites = useMemo(() => sortRows(sites, siteSort, {
    total_owed: 'number', overdue_owed: 'number', open_items: 'number', oldest_days: 'number',
  }), [sites, siteSort])
  const sortedPms = useMemo(() => sortRows(pms, pmSort, {
    total_owed: 'number', overdue_owed: 'number', sites: 'number',
  }), [pms, pmSort])
  const sortedVendors = useMemo(() => sortRows(vendors, venSort, {
    total_owed: 'number', overdue_owed: 'number', oldest_days: 'number',
  }), [vendors, venSort])

  const ageingSorted = useMemo(
    () => [...ageing].sort((a, b) => a.bucket_order - b.bucket_order), [ageing])

  // Top ten vendors, with the long tail collapsed into one honest bar rather
  // than eighty unreadable slivers.
  const vendorChart = useMemo(() => {
    const rows = [...vendors].sort((a, b) => Number(b.total_owed) - Number(a.total_owed))
    const top = rows.slice(0, 10).map((v) => ({
      name: v.vendor_name?.slice(0, 22) ?? v.vendor_id,
      invoiced: Number(v.invoiced_owed), uninvoiced: Number(v.uninvoiced_owed),
    }))
    const restTotal = rows.slice(10).reduce((a, v) => a + Number(v.total_owed), 0)
    if (restTotal > 0) {
      top.push({ name: `${rows.length - 10} others`, invoiced: restTotal, uninvoiced: 0 })
    }
    return top
  }, [vendors])

  const siteChart = useMemo(() => sites.map((s) => ({
    name: s.site_name?.slice(0, 18) ?? s.site_id,
    invoiced: Number(s.invoiced_owed),
    uninvoiced: Number(s.uninvoiced_owed),
    overdue: Number(s.overdue_owed),
  })).sort((a, b) => (b.invoiced + b.uninvoiced) - (a.invoiced + a.uninvoiced)), [sites])

  const pmChart = useMemo(() => pms.map((p) => ({
    name: p.pm_name?.split(' ')[0] ?? p.pm_key,
    owed: Number(p.total_owed), overdue: Number(p.overdue_owed),
  })).sort((a, b) => b.owed - a.owed), [pms])

  const trendData = useMemo(() => trend.map((t) => ({
    ...t,
    total_owed: Number(t.total_owed),
    exact: t.source === 'SNAPSHOT' ? Number(t.total_owed) : null,
  })), [trend])

  const anyExact = trendData.some((t) => t.source === 'SNAPSHOT')

  // The concentration question a CFO always asks: how much of what we owe sits
  // with a handful of vendors? Single-supplier dependence is a real risk.
  const concentration = useMemo(() => {
    const total = Number(c?.total_owed ?? 0)
    if (!total) return null
    const rows = [...vendors].sort((a, b) => Number(b.total_owed) - Number(a.total_owed))
    const top3 = rows.slice(0, 3).reduce((a, v) => a + Number(v.total_owed), 0)
    return { pct: (top3 / total) * 100, names: rows.slice(0, 3).map((v) => v.vendor_name) }
  }, [vendors, c])
  // 0212: the alert level is a System Setting (40% until changed).
  const { vendorConcentrationPct } = useCompanySettings()

  if (!mayUse) {
    return <NotPermitted what="the liabilities dashboard"
                         who="the CMD, the CXOs, the Project Head, Accounts, the Head of Stores, the Procurement Manager, and each Project Manager for his own sites" />
  }

  async function takeSnapshot() {
    setSnapping(true)
    // Records BOTH series. Since the board panel started keeping a daily stock
    // value, a button that recorded only liabilities would fill half the gap
    // and leave the other half missing - with nothing on this screen to say so.
    // One button, one nightly job, both measures.
    const { data, error } = await supabase.rpc('rpc_snapshot_daily')
    setSnapping(false)
    if (error) { toast.error(error.message); return }
    toast.success(`Today's position recorded. ${data ?? ''} The trend is now exact from today.`)
    refreshTrend()
    refreshHealth()   // so the warning clears without a page reload
  }

  // Same helper the Reports screen uses: the edge function streams a workbook
  // back as a blob, it does not hand out a URL.
  async function exportIt(report: string, label: string) {
    if (exporting) return
    setExporting(report)
    try {
      await downloadFromFunction('export-report', { report, filters: {} }, `${report}.xlsx`)
      toast.success('Workbook downloaded.')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : `Could not export ${label}.`)
    } finally { setExporting(null) }
  }

  const scopeOptions = scope === 'SITE'
    ? sites.map((s) => ({ value: s.site_id, label: s.site_name }))
    : scope === 'PM'
      ? pms.map((p) => ({ value: p.pm_key, label: p.pm_name }))
      : scope === 'VENDOR'
        ? [...vendors].sort((a, b) => Number(b.total_owed) - Number(a.total_owed))
            .slice(0, 50).map((v) => ({ value: v.vendor_id, label: v.vendor_name }))
        : []

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Liabilities</h1>
          <p className="text-sm text-slate-500">
            {isPm
              ? 'What OVAL owes its vendors against YOUR sites right now — by site, '
                + 'by vendor and by age. Sites you do not manage are not counted in '
                + 'any figure on this page.'
              : 'What OVAL owes its vendors right now — company-wide, by site, by Project '
                + 'Manager and by vendor.'}
          </p>
        </div>
        {mayRecord && (
          <div className="flex flex-wrap gap-2">
            <button className="btn-secondary btn-sm" onClick={() => void takeSnapshot()}
                    disabled={snapping}
                    title="Normally automatic at 22:00. Use this only if the nightly job did not run.">
              <RefreshCw className={`mr-1 h-4 w-4 ${snapping ? 'animate-spin' : ''}`} />
              {snapping ? 'Recording…' : "Record today's position"}
            </button>
          </div>
        )}
      </div>

      {/*
        Shown to the administrator only. Everyone else — the CMD included —
        sees a dashboard, not a machine that needs minding. If the nightly job
        stopped a week ago, this is the screen that says so, and it says it
        without depending on the job itself.
      */}
      {mayRecord && snap && (
        // Two different failures, and the second one used to hide.
        //
        //   is_overdue          the latest position is older than it should be —
        //                       the job has stopped running.
        //   missing_last_30_days a hole further back. The job failed one night
        //                       and worked the next, so the LATEST snapshot is
        //                       current and nothing looked stale. Without this
        //                       the gap was invisible from the morning after.
        (snap.is_overdue || Number(snap.missing_last_30_days ?? 0) > 0) ? (
          <Alert tone="warning"
                 title={snap.is_overdue
                   ? (snap.nights_missed && snap.nights_missed > 1
                       ? `The nightly position has not been recorded for ${snap.nights_missed} nights`
                       : 'Last night’s position was not recorded')
                   : `${snap.missing_last_30_days} night${snap.missing_last_30_days === 1 ? '' : 's'} missing from the record`}>
            {snap.is_overdue ? (
              <>
                The 22:00 job did not run. Press <strong>Record today&apos;s position</strong> above
                to fill today in.{' '}
                {snap.last_snapshot_date
                  ? <>The last one on file is <strong>{ddmmyyyy(snap.last_snapshot_date)}</strong>.</>
                  : <>No position has ever been recorded.</>}
              </>
            ) : (
              <>
                Recording is up to date — the last one is{' '}
                <strong>{ddmmyyyy(snap.last_snapshot_date)}</strong> — but the job missed{' '}
                {snap.missing_last_30_days === 1 ? 'a night' : 'some nights'} earlier. Those days
                cannot be recovered: a position can only be recorded on the day it applies. The
                chart falls back to a reconstructed figure for them.
              </>
            )}
            {snap.missing_last_30_days > 1 && (
              <> {snap.missing_last_30_days} nights are missing in the last 30 — if this keeps
              happening the scheduled job needs looking at, not just today&apos;s gap.</>
            )}
          </Alert>
        ) : (
          <p className="text-xs text-slate-500">
            Position last recorded{' '}
            <strong>{ddmmyyyy(snap.last_snapshot_date)}</strong>
            {snap.last_taken_at && ` at ${new Date(snap.last_taken_at).toLocaleTimeString('en-IN',
              { hour: '2-digit', minute: '2-digit' })}`}
            {' '}· automatic, every night at 22:00. Nothing to do.
          </p>
        )
      )}

      {(l1 || l2) && <Loading label="Working out what is owed…" />}

      {!l1 && c && (
        <>
          {/* ---------- The headline ---------- */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Total owed" value={rupees(c.total_owed)}
                  sub={`${c.open_items} open item(s) · ${c.vendors} vendor(s)`}
                  icon={<Wallet className="h-5 w-5" />} />
            <Stat label="Invoiced — bill on file" value={rupees(c.invoiced_owed)}
                  sub="a confirmed trade payable"
                  icon={<Building2 className="h-5 w-5" />} />
            <Stat label="Received, not yet billed" value={rupees(c.uninvoiced_owed)}
                  sub="goods in, invoice awaited — a provision"
                  tone="warn" icon={<Truck className="h-5 w-5" />} />
            <Stat label="Overdue" value={rupees(c.overdue_owed)}
                  sub={c.overdue_items > 0
                    ? `${c.overdue_items} item(s) past due date`
                    : 'nothing past its due date'}
                  tone={Number(c.overdue_owed) > 0 ? 'bad' : 'good'}
                  icon={<AlertTriangle className="h-5 w-5" />} />
          </div>

          <Alert tone="info" title="Where these numbers come from">
            Every figure on this screen is the same <strong>outstanding</strong> the
            Payables register shows — rolled up, never recalculated. Record a payment
            there and it moves here on the next refresh. There is no separate liability
            ledger that could drift out of step.
          </Alert>

          {/* ---------- Trend ---------- */}
          <Card
            title="How the liability has moved"
            subtitle={`Month-end position${scope !== 'COMPANY' ? ' for the selection below' : ', company-wide'}`}
            actions={
              <div className="flex flex-wrap gap-2">
                {/*
                  "Company-wide" is not offered to a Project Manager, and
                  neither is "One Project Manager".

                  The database would narrow either request to his own portfolio
                  anyway — fn_liability_trend is SECURITY DEFINER and enforces
                  it by hand. But a dropdown that says "Company-wide" while the
                  chart quietly draws three sites is worse than no dropdown: he
                  reads the label, not the policy. Offer him only the questions
                  the system will actually answer.
                */}
                <Select value={scope} className="w-40"
                        onChange={(e) => { setScope(e.target.value as any); setScopeId('') }}
                        options={isPm
                          ? [
                              { value: 'PM', label: 'All my sites' },
                              { value: 'SITE', label: 'One of my sites' },
                              { value: 'VENDOR', label: 'One vendor' },
                            ]
                          : [
                              { value: 'COMPANY', label: 'Company-wide' },
                              { value: 'SITE', label: 'One site' },
                              { value: 'PM', label: 'One Project Manager' },
                              { value: 'VENDOR', label: 'One vendor' },
                            ]} />
                {scope !== 'COMPANY' && (
                  <Select value={scopeId} className="w-52"
                          onChange={(e) => setScopeId(e.target.value)}
                          placeholder="Choose…" options={scopeOptions} />
                )}
                <Select value={String(months)} className="w-32"
                        onChange={(e) => setMonths(Number(e.target.value))}
                        options={[
                          { value: '6', label: '6 months' },
                          { value: '12', label: '12 months' },
                          { value: '18', label: '18 months' },
                          { value: '24', label: '24 months' },
                        ]} />
              </div>
            }>
            {l3 && <Loading label="Rebuilding the trend…" />}
            {!l3 && scope !== 'COMPANY' && !scopeId && (
              <p className="py-8 text-center text-sm text-slate-500">
                Choose one from the list above to see its trend.
              </p>
            )}
            {!l3 && (scope === 'COMPANY' || scopeId) && (
              <>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={trendData} margin={{ top: 8, right: 8, left: 8, bottom: 4 }}>
                    <defs>
                      <linearGradient id="owedFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={OVAL} stopOpacity={0.28} />
                        <stop offset="100%" stopColor={OVAL} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#DFE6E3" vertical={false} />
                    <XAxis dataKey="period_label" tick={{ fontSize: 11, fill: '#71807C' }}
                           tickLine={false} axisLine={{ stroke: '#DFE6E3' }} />
                    <YAxis tickFormatter={lakh} tick={{ fontSize: 11, fill: '#71807C' }}
                           tickLine={false} axisLine={false} width={64} />
                    <Tooltip content={<ChartTip />} />
                    <Area type="monotone" dataKey="total_owed" name="Owed"
                          stroke={OVAL} strokeWidth={2} fill="url(#owedFill)" />
                    {anyExact && (
                      <Line type="monotone" dataKey="exact" name="Exact (snapshot)"
                            stroke={OVAL} strokeWidth={0}
                            dot={{ r: 4, fill: OVAL, stroke: '#fff', strokeWidth: 2 }}
                            connectNulls={false} legendType="circle" />
                    )}
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </ComposedChart>
                </ResponsiveContainer>

                <div className="mt-2 flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                  <p className="text-xs text-slate-600">
                    {anyExact
                      ? 'Ringed points are exact daily snapshots, recorded automatically at 22:00 each day. The rest are reconstructed from the dates recorded against each receipt, payment and reconciliation.'
                      : 'These points are reconstructed from the dates recorded against each receipt, payment, reconciliation and credit note — nobody recorded when billing was closed on each payable, so a past month cannot be restated perfectly. The arithmetic is anchored so the latest point equals today exactly. From tonight, an exact position is recorded automatically at 22:00; the reconstructed part scrolls off the chart as the weeks pass.'}
                  </p>
                </div>
              </>
            )}
          </Card>

          {/* ---------- Ageing + concentration ---------- */}
          <div className="grid gap-4 lg:grid-cols-3">
            <Card title="Ageing" subtitle="The 0/30/60/90 ladder an auditor expects"
                  className="lg:col-span-1">
              {ageingSorted.length === 0
                ? <p className="py-8 text-center text-sm text-slate-500">Nothing outstanding.</p>
                : (
                  <>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={ageingSorted} dataKey="total_owed" nameKey="ageing_bucket"
                             cx="50%" cy="50%" innerRadius={48} outerRadius={80} paddingAngle={2}>
                          {ageingSorted.map((a) => (
                            <Cell key={a.ageing_bucket} fill={AGE_COLOURS[a.ageing_bucket] ?? SLATE} />
                          ))}
                        </Pie>
                        <Tooltip content={<ChartTip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <ul className="mt-1 space-y-1">
                      {ageingSorted.map((a) => (
                        <li key={a.ageing_bucket} className="flex items-center justify-between text-xs">
                          <span className="flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-sm"
                                  style={{ background: AGE_COLOURS[a.ageing_bucket] ?? SLATE }} />
                            {a.ageing_bucket}
                          </span>
                          <span className="tabular-nums font-semibold">{rupees(a.total_owed)}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
            </Card>

            <Card title="Where the money is owed" subtitle="By site — invoiced against not yet billed"
                  className="lg:col-span-2">
              {siteChart.length === 0
                ? <p className="py-8 text-center text-sm text-slate-500">Nothing outstanding.</p>
                : (
                  <ResponsiveContainer width="100%" height={Math.max(220, siteChart.length * 42)}>
                    <BarChart data={siteChart} layout="vertical"
                              margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#DFE6E3" horizontal={false} />
                      <XAxis type="number" tickFormatter={lakh}
                             tick={{ fontSize: 11, fill: '#71807C' }} tickLine={false} axisLine={false} />
                      <YAxis type="category" dataKey="name" width={120}
                             tick={{ fontSize: 11, fill: '#33423E' }} tickLine={false} axisLine={false} />
                      <Tooltip content={<ChartTip />} cursor={{ fill: '#F1F4F3' }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="invoiced" name="Invoiced" stackId="a" fill={OVAL} radius={[0, 0, 0, 0]} />
                      <Bar dataKey="uninvoiced" name="Received, not billed" stackId="a"
                           fill={OVAL_LIGHT} radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
            </Card>
          </div>

          {/* ---------- PM + vendor ---------- */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="By Project Manager" subtitle="Each manager's exposure across the sites he holds">
              {pmChart.length === 0
                ? <p className="py-8 text-center text-sm text-slate-500">Nothing outstanding.</p>
                : (
                  <ResponsiveContainer width="100%" height={Math.max(200, pmChart.length * 54)}>
                    <BarChart data={pmChart} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#DFE6E3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#33423E' }}
                             tickLine={false} axisLine={{ stroke: '#DFE6E3' }} />
                      <YAxis tickFormatter={lakh} tick={{ fontSize: 11, fill: '#71807C' }}
                             tickLine={false} axisLine={false} width={64} />
                      <Tooltip content={<ChartTip />} cursor={{ fill: '#F1F4F3' }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="owed" name="Owed" fill={OVAL} radius={[3, 3, 0, 0]} />
                      <Bar dataKey="overdue" name="Of which overdue" fill={RED} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
            </Card>

            <Card title="Largest vendor balances" subtitle="Top ten, with the tail collapsed">
              {vendorChart.length === 0
                ? <p className="py-8 text-center text-sm text-slate-500">Nothing outstanding.</p>
                : (
                  <ResponsiveContainer width="100%" height={Math.max(200, vendorChart.length * 34)}>
                    <BarChart data={vendorChart} layout="vertical"
                              margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#DFE6E3" horizontal={false} />
                      <XAxis type="number" tickFormatter={lakh}
                             tick={{ fontSize: 11, fill: '#71807C' }} tickLine={false} axisLine={false} />
                      <YAxis type="category" dataKey="name" width={140}
                             tick={{ fontSize: 11, fill: '#33423E' }} tickLine={false} axisLine={false} />
                      <Tooltip content={<ChartTip />} cursor={{ fill: '#F1F4F3' }} />
                      <Bar dataKey="invoiced" name="Invoiced" stackId="a" fill={OVAL} />
                      <Bar dataKey="uninvoiced" name="Received, not billed" stackId="a"
                           fill={OVAL_LIGHT} radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
            </Card>
          </div>

          {concentration && concentration.pct >= vendorConcentrationPct && (
            <Alert tone="warning" title={`${concentration.pct.toFixed(0)}% of what the company owes sits with three vendors`}>
              {concentration.names.filter(Boolean).join(', ')}. Concentration is not a
              fault in itself — it often means good rates. It is worth knowing because a
              dispute with any one of them affects a large share of the payment run.
            </Alert>
          )}

          {/* ---------- Tables ---------- */}
          <Card title="Site by site"
                actions={isPm ? undefined :
                  <button className="btn-secondary btn-sm" disabled={!!exporting}
                          onClick={() => void exportIt('liabilities_by_site', 'the site-wise liabilities')}>
                    <Download className="mr-1 h-4 w-4" /> Export
                  </button>
                }>
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <Th id="site_name" sort={siteSort} onSort={siteToggle}>Site</Th>
                    <Th id="pm_name" sort={siteSort} onSort={siteToggle}>Project Manager</Th>
                    <Th id="open_items" sort={siteSort} onSort={siteToggle} align="right">Items</Th>
                    <Th id="total_owed" sort={siteSort} onSort={siteToggle} align="right">Owed</Th>
                    <th className="text-right">Invoiced</th>
                    <th className="text-right">Not billed</th>
                    <Th id="overdue_owed" sort={siteSort} onSort={siteToggle} align="right">Overdue</Th>
                    <Th id="oldest_days" sort={siteSort} onSort={siteToggle} align="right">Oldest</Th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSites.map((s) => (
                    <tr key={s.site_id} className="hover:bg-oval-50">
                      <td className="font-medium">{s.site_name}</td>
                      <td className="text-slate-600">{s.pm_name ?? <span className="text-amber-700">Unassigned</span>}</td>
                      <td className="text-right tabular-nums">{s.open_items}</td>
                      <td className="text-right font-semibold tabular-nums">{rupees(s.total_owed)}</td>
                      <td className="text-right tabular-nums text-slate-600">{rupees(s.invoiced_owed)}</td>
                      <td className="text-right tabular-nums text-slate-600">{rupees(s.uninvoiced_owed)}</td>
                      <td className={`text-right tabular-nums ${Number(s.overdue_owed) > 0 ? 'font-semibold text-red-700' : 'text-slate-400'}`}>
                        {Number(s.overdue_owed) > 0 ? rupees(s.overdue_owed) : '—'}
                      </td>
                      <td className="text-right tabular-nums text-slate-500">
                        {s.oldest_days != null ? `${s.oldest_days}d` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card title="Project Manager by Project Manager"
                actions={isPm ? undefined :
                  <button className="btn-secondary btn-sm" disabled={!!exporting}
                          onClick={() => void exportIt('liabilities_by_pm', 'the manager-wise liabilities')}>
                    <Download className="mr-1 h-4 w-4" /> Export
                  </button>
                }>
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <Th id="pm_name" sort={pmSort} onSort={pmToggle}>Project Manager</Th>
                    <Th id="sites" sort={pmSort} onSort={pmToggle} align="right">Sites</Th>
                    <th className="text-right">Vendors</th>
                    <Th id="total_owed" sort={pmSort} onSort={pmToggle} align="right">Owed</Th>
                    <th className="text-right">Invoiced</th>
                    <th className="text-right">Not billed</th>
                    <Th id="overdue_owed" sort={pmSort} onSort={pmToggle} align="right">Overdue</Th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPms.map((p) => (
                    <tr key={p.pm_key} className="hover:bg-oval-50">
                      <td className="font-medium">
                        <span className="flex items-center gap-2">
                          <HardHat className="h-4 w-4 text-slate-300" />
                          {p.pm_name}
                        </span>
                      </td>
                      <td className="text-right tabular-nums">{p.sites}</td>
                      <td className="text-right tabular-nums">{p.vendors}</td>
                      <td className="text-right font-semibold tabular-nums">{rupees(p.total_owed)}</td>
                      <td className="text-right tabular-nums text-slate-600">{rupees(p.invoiced_owed)}</td>
                      <td className="text-right tabular-nums text-slate-600">{rupees(p.uninvoiced_owed)}</td>
                      <td className={`text-right tabular-nums ${Number(p.overdue_owed) > 0 ? 'font-semibold text-red-700' : 'text-slate-400'}`}>
                        {Number(p.overdue_owed) > 0 ? rupees(p.overdue_owed) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card title="Vendor by vendor"
                subtitle="Every vendor with a balance, largest first"
                actions={isPm ? undefined :
                  <div className="flex gap-2">
                    <button className="btn-secondary btn-sm" disabled={!!exporting}
                            onClick={() => void exportIt('liabilities_by_vendor', 'the vendor-wise liabilities')}>
                      <Download className="mr-1 h-4 w-4" /> Export
                    </button>
                    <button className="btn-secondary btn-sm" disabled={!!exporting}
                            onClick={() => void exportIt('liabilities_detail', 'the full liability detail')}>
                      <Download className="mr-1 h-4 w-4" /> Full detail
                    </button>
                  </div>
                }>
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <Th id="vendor_name" sort={venSort} onSort={venToggle}>Vendor</Th>
                    <th>GSTIN</th>
                    <th className="text-right">Items</th>
                    <th className="text-right">Sites</th>
                    <Th id="total_owed" sort={venSort} onSort={venToggle} align="right">Owed</Th>
                    <Th id="overdue_owed" sort={venSort} onSort={venToggle} align="right">Overdue</Th>
                    <Th id="oldest_days" sort={venSort} onSort={venToggle} align="right">Oldest</Th>
                    <th>Next due</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedVendors.map((v) => (
                    <tr key={v.vendor_id} className="hover:bg-oval-50">
                      <td className="font-medium">{v.vendor_name}</td>
                      <td className="font-mono text-[11px] text-slate-500">{v.vendor_gstin ?? '—'}</td>
                      <td className="text-right tabular-nums">{v.open_items}</td>
                      <td className="text-right tabular-nums">{v.sites}</td>
                      <td className="text-right font-semibold tabular-nums">{rupees(v.total_owed)}</td>
                      <td className={`text-right tabular-nums ${Number(v.overdue_owed) > 0 ? 'font-semibold text-red-700' : 'text-slate-400'}`}>
                        {Number(v.overdue_owed) > 0 ? rupees(v.overdue_owed) : '—'}
                      </td>
                      <td className="text-right tabular-nums text-slate-500">
                        {v.oldest_days != null ? `${v.oldest_days}d` : '—'}
                      </td>
                      <td className="whitespace-nowrap text-slate-600">
                        {v.next_due ? ddmmyyyy(v.next_due) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* The export service serves the board's pack only - a Project
              Manager would press these and be refused. Hidden for him
              (11-09-2026); every other role sees them exactly as before. */}
          {!isPm && <Card title="Export the whole position">
            <div className="flex flex-wrap gap-2">
              <button className="btn-secondary btn-sm" disabled={!!exporting}
                      onClick={() => void exportIt('liabilities_company', 'the company summary')}>
                <Download className="mr-1 h-4 w-4" /> Company-wide
              </button>
              <button className="btn-secondary btn-sm" disabled={!!exporting}
                      onClick={() => void exportIt('liabilities_by_site', 'the site-wise liabilities')}>
                <Download className="mr-1 h-4 w-4" /> Site-wise
              </button>
              <button className="btn-secondary btn-sm" disabled={!!exporting}
                      onClick={() => void exportIt('liabilities_by_pm', 'the manager-wise liabilities')}>
                <Download className="mr-1 h-4 w-4" /> Project-Manager-wise
              </button>
              <button className="btn-secondary btn-sm" disabled={!!exporting}
                      onClick={() => void exportIt('liabilities_by_vendor', 'the vendor-wise liabilities')}>
                <Download className="mr-1 h-4 w-4" /> Vendor-wise
              </button>
              <button className="btn-secondary btn-sm" disabled={!!exporting}
                      onClick={() => void exportIt('liabilities_detail', 'the full liability detail')}>
                <Download className="mr-1 h-4 w-4" /> Every open item
              </button>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Each export carries the same figures as the screen, as at the moment you
              press the button. <strong>Every open item</strong> is the one to send to
              the auditors — one row per payable with its receipt, its bill, what has
              been paid and what is left.
            </p>
          </Card>}

          <Alert tone="info" title="Two things worth knowing before quoting these numbers">
            <p>
              <strong>Received but not yet billed is a provision, not a bill.</strong>{' '}
              {rupees(c.uninvoiced_owed)} of the total is material that has arrived at
              site and been accepted, where the vendor has not yet sent his invoice. The
              company owes it, but the amount can still move when the bill arrives.
            </p>
            <p className="mt-2">
              <strong>This is a materials sub-ledger, not the books.</strong> Payroll,
              statutory dues, loans and every other liability live in Tally. What this
              screen shows is trade payables arising from material purchases — which is
              what it is for, and all it claims to be.
            </p>
          </Alert>
        </>
      )}
    </div>
  )
}
